import { Fragment } from "react"
import type { ReactElement } from "react"

import isEmpty from "lodash-es/isEmpty"

import {
    ESpecialChar,
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    SpecialChars,
    Typography
} from "@/3514/components"
import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import { buildCssClassName } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { emptyRenderCallback } from "@/shared/types/functions"

import classes from "./session-info.styles"
import { type TSessionInfoHeaderProps as TProps } from "./session-info.types"
import utils from "./session-info.utils"

function SessionInfoHeader({
    data,
    renderHeaderAvatar: renderAvatar,
    renderHeaderNameToolip = emptyRenderCallback,
    isAccordionExpanded: isExpanded = false,
    className
}: TProps): ReactElement {
    const { t }: IUseTranslation = useTranslation()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    return (
        <div
            className={buildCssClassName(
                classes.header.container({
                    isExpanded,
                    isCancelled: data.session.isCancelled,
                    isDateless: isEmpty(data.session.startDate)
                }),
                {
                    [className]: className
                }
            )}
        >
            <div className={classes.header.image()}>{renderAvatar()}</div>

            <div className={classes.header.col()}>
                <div data-for={data.session.id} data-tip={data.session.id}>
                    <Typography
                        text={utils.getFormattedDate(data.session.startDate)}
                        color={ETypographyColor.Black}
                        tag={ETypographyTag.Paragraph}
                        size={breakpoints.xs || breakpoints.sm ? ETypographySize.Small : ETypographySize.Medium}
                        weight={ETypographyWeight.Bold}
                    />
                </div>

                <Typography
                    text={
                        <Fragment>
                            {data.session.duration}
                            {SpecialChars[ESpecialChar.EmptySpace]}
                            {t("common.min")}
                        </Fragment>
                    }
                    color={ETypographyColor.Dark}
                    tag={ETypographyTag.Paragraph}
                    size={ETypographySize.Small}
                />
            </div>

            <div />

            {renderHeaderNameToolip()}
        </div>
    )
}

SessionInfoHeader.displayName = "SessionInfoHeader"

export default SessionInfoHeader
